<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<script lang="ts">
  import {
    type Colors,
    type GameraGridRow,
    type GameraTeamReference,
    type LineupChangeEvent,
  } from '@statmuse/core/gamera'
  import { range, max, round } from 'lodash-es'
  import {
    lineScore,
    playerStats,
    teamStats,
    players,
    plays,
    selectedId,
    starters,
  } from './stores'
  import Grid from '@components/grid.svelte'

  export let playerIds: number[]
  export let teamKey: 'away' | 'home'
  export let team: GameraTeamReference | undefined
  export let live = false

  let selectedTab = 0

  const orderingByPosition = (x?: string) => {
    return (
      {
        '': 0,
        SMALL_FORWARD: 1,
        POWER_FORWARD: 2,
        CENTER: 3,
        SHOOTING_GUARD: 4,
        POINT_GUARD: 5,
      }[x ?? ''] ?? 0
    )
  }

  const onClickSelectPlayer = (row: GameraGridRow) => () =>
    selectedId.set({
      id: row.NAME?.meta?.id ?? 0,
      alignment: teamKey,
    })

  const playerColumns = (title: string) => [
    {
      title,
      type: 'string',
      rowItemKey: 'NAME',
    },
    {
      title: 'MIN',
      type: 'number',
      rowItemKey: 'MIN',
    },
    {
      title: 'PTS',
      type: 'number',
      rowItemKey: 'PTS',
    },
    {
      title: 'REB (O)',
      type: 'number',
      rowItemKey: 'REB',
    },
    {
      title: 'AST',
      type: 'number',
      rowItemKey: 'AST',
    },
    {
      title: 'STL',
      type: 'number',
      rowItemKey: 'STL',
    },
    {
      title: 'BLK',
      type: 'number',
      rowItemKey: 'BLK',
    },
    {
      title: '+/-',
      type: 'number',
      rowItemKey: '+/-',
    },
    {
      title: 'TOV',
      type: 'number',
      rowItemKey: 'TOV',
    },
    {
      title: 'PF',
      type: 'number',
      rowItemKey: 'PF',
    },
    {
      title: 'FG',
      type: 'number',
      rowItemKey: 'FG',
    },
    {
      title: '3P',
      type: 'number',
      rowItemKey: '3P',
    },
    {
      title: 'FT',
      type: 'number',
      rowItemKey: 'FT',
    },
    {
      title: 'TS%',
      type: 'number',
      rowItemKey: 'TS%',
    },
  ]

  const teamColumns = [
    {
      title: 'Team',
      type: 'string',
      rowItemKey: 'NAME',
    },
    {
      title: 'PTS',
      type: 'number',
      rowItemKey: 'PTS',
    },
    {
      title: 'REB (O)',
      type: 'number',
      rowItemKey: 'REB',
    },
    {
      title: 'AST',
      type: 'number',
      rowItemKey: 'AST',
    },
    {
      title: 'STL',
      type: 'number',
      rowItemKey: 'STL',
    },
    {
      title: 'BLK',
      type: 'number',
      rowItemKey: 'BLK',
    },
    {
      title: 'TOV',
      type: 'number',
      rowItemKey: 'TOV',
    },
    {
      title: 'FG',
      type: 'number',
      rowItemKey: 'FG',
    },
    {
      title: '3P',
      type: 'number',
      rowItemKey: '3P',
    },
    {
      title: 'FT',
      type: 'number',
      rowItemKey: 'FT',
    },
    {
      title: 'TS%',
      type: 'number',
      rowItemKey: 'TS%',
    },
  ]

  const rowKeyFn = (row: GameraGridRow) =>
    playerColumns('').reduce(
      (acc, x) => acc + `${row[x.rowItemKey].display},`,
      row.NAME.display,
    )

  $: hasPeriodSplits =
    live || !!$playerStats[teamKey]?.splits?.[0].splits?.[0].splits

  $: starterPlayerIds = $starters[teamKey].sort(
    (a, b) =>
      orderingByPosition($players[a]?.position) -
      orderingByPosition($players[b]?.position),
  )

  $: benchPlayerIds = playerIds.filter((p) => starterPlayerIds.indexOf(p) < 0)

  $: selectorWidth = round(
    (1 / (max([4, $lineScore.away?.periods?.length]) + 1)) * 100,
    1,
  )

  $: tabs = [
    { label: 'All', value: 0 },
    ...range(1, (max([4, $lineScore.away?.periods?.length]) ?? 4) + 1).map(
      (x) => ({
        label: `${x <= 4 ? x : x === 5 ? '' : x - 4}${x > 4 ? 'OT' : 'Q'}`,
        value: x,
      }),
    ),
  ]

  $: playerLookupKeys =
    $playerStats[teamKey]?.splits?.[0].splits?.map((s) => ({
      statsLookupKey:
        selectedTab === 0
          ? s.statsLookupKey
          : s.splits?.find((x) => x.period === selectedTab)?.statsLookupKey,

      playerId: s.playerId,
    })) ?? []

  $: stats = $playerStats[teamKey]?.statsLookup

  $: starterRows =
    starterPlayerIds
      ?.map((id: number) => {
        const player = $players[id]
        const lookupKey = playerLookupKeys.find(
          (p) => p.playerId === id,
        )?.statsLookupKey
        const statmodel = stats?.[lookupKey ?? '']

        if (player) {
          return {
            NAME: {
              display: player?.longName ?? '',
              value: player?.longName ?? '',
              meta: {
                id: Number(player?.id),
                shortDisplay: player?.shortName,
                position: player?.uniformNumber
                  ? `#${player.uniformNumber}`
                  : undefined,
              },
            },
            MIN: statmodel?.MinutesPlayed ?? { display: '', value: 0 },
            PTS: statmodel?.Points ?? { display: '', value: 0 },
            REB: statmodel?.Rebounds
              ? {
                  display: `${statmodel?.Rebounds?.value ?? 0} (${
                    statmodel?.OffensiveRebounds?.value ?? 0
                  })`,
                  value: statmodel?.Rebounds?.value ?? 0,
                }
              : { display: '', value: 0 },
            AST: statmodel?.Assists ?? { display: '', value: 0 },
            STL: statmodel?.Steals ?? { display: '', value: 0 },
            BLK: statmodel?.Blocks ?? { display: '', value: 0 },
            '+/-': statmodel?.PlusMinus ?? { display: '', value: 0 },
            TOV: statmodel?.Turnovers ?? { display: '', value: 0 },
            PF: statmodel?.PersonalFouls ?? { display: '', value: 0 },
            FG: statmodel?.FieldGoalsAttempted
              ? {
                  display: `${statmodel?.FieldGoalsMade?.value ?? 0}-${
                    statmodel?.FieldGoalsAttempted?.value ?? 0
                  }`,
                  value: statmodel?.FieldGoalsMade?.value ?? 0,
                }
              : { display: '', value: 0 },
            '3P': statmodel?.ThreePointsAttempted
              ? {
                  display: `${statmodel?.ThreePointsMade?.value ?? 0}-${
                    statmodel?.ThreePointsAttempted?.value ?? 0
                  }`,
                  value: statmodel?.ThreePointsMade?.value ?? 0,
                }
              : { display: '', value: 0 },
            FT: statmodel?.FreeThrowsAttempted
              ? {
                  display: `${statmodel?.FreeThrowsMade?.value ?? 0}-${
                    statmodel?.FreeThrowsAttempted?.value ?? 0
                  }`,
                  value: statmodel?.FreeThrowsMade?.value ?? 0,
                }
              : { display: '', value: 0 },
            'TS%': statmodel?.TrueShootingPercentage ?? {
              display: '',
              value: 0,
            },
          }
        }
      })
      .filter((x) => !!x) ?? []

  $: benchRows =
    benchPlayerIds
      ?.map((id: number) => {
        const player = $players[id]
        const lookupKey = playerLookupKeys.find(
          (p) => p.playerId === id,
        )?.statsLookupKey
        const statmodel = stats?.[lookupKey ?? '']
        if (player) {
          return {
            NAME: {
              display: player?.longName ?? '',
              value: player?.longName ?? '',
              meta: {
                id: Number(player?.id),
                shortDisplay: player?.shortName,
                position: player?.uniformNumber
                  ? `#${player.uniformNumber}`
                  : undefined,
              },
            },
            MIN: statmodel?.MinutesPlayed ?? { display: '', value: 0 },
            PTS: statmodel?.Points ?? { display: '', value: 0 },
            REB: statmodel?.Rebounds
              ? {
                  display: `${statmodel?.Rebounds?.value ?? 0} (${
                    statmodel?.OffensiveRebounds?.value ?? 0
                  })`,
                  value: statmodel?.Rebounds?.value ?? 0,
                }
              : { display: '', value: 0 },
            AST: statmodel?.Assists ?? { display: '', value: 0 },
            STL: statmodel?.Steals ?? { display: '', value: 0 },
            BLK: statmodel?.Blocks ?? { display: '', value: 0 },
            '+/-': statmodel?.PlusMinus ?? { display: '', value: 0 },
            TOV: statmodel?.Turnovers ?? { display: '', value: 0 },
            PF: statmodel?.PersonalFouls ?? { display: '', value: 0 },
            FG: statmodel?.FieldGoalsAttempted
              ? {
                  display: `${statmodel?.FieldGoalsMade?.value ?? 0}-${
                    statmodel?.FieldGoalsAttempted?.value ?? 0
                  }`,
                  value: statmodel?.FieldGoalsMade?.value ?? 0,
                }
              : { display: '', value: 0 },
            '3P': statmodel?.ThreePointsAttempted
              ? {
                  display: `${statmodel?.ThreePointsMade?.value ?? 0}-${
                    statmodel?.ThreePointsAttempted?.value ?? 0
                  }`,
                  value: statmodel?.ThreePointsMade?.value ?? 0,
                }
              : { display: '', value: 0 },
            FT: statmodel?.FreeThrowsAttempted
              ? {
                  display: `${statmodel?.FreeThrowsMade?.value ?? 0}-${
                    statmodel?.FreeThrowsAttempted?.value ?? 0
                  }`,
                  value: statmodel?.FreeThrowsMade?.value ?? 0,
                }
              : { display: '', value: 0 },
            'TS%': statmodel?.TrueShootingPercentage ?? {
              display: '',
              value: 0,
            },
          }
        }
      })
      .filter((x) => !!x)
      .sort((a, b) => b.MIN.value - a.MIN.value) ?? []

  $: teamStatsLookupKey =
    selectedTab === 0
      ? $teamStats[teamKey]?.splits?.[0].statsLookupKey
      : $teamStats[teamKey]?.splits?.[0].splits?.find(
          (s) => s.period === selectedTab,
        )?.statsLookupKey
  $: teamStatModel = $teamStats[teamKey]?.statsLookup

  $: teamRows = [
    {
      NAME: {
        display: team?.abbreviation ?? '',
        value: team?.abbreviation,
        imageUrl: team?.logoImageUrl,
        meta: {
          id: Number(team?.teamId),
        },
      },
      PTS: teamStatModel?.[teamStatsLookupKey ?? '']?.Points ?? {
        display: '',
        value: 0,
      },
      REB: {
        display: `${
          teamStatModel?.[teamStatsLookupKey ?? '']?.Rebounds?.value ?? 0
        } (${
          teamStatModel?.[teamStatsLookupKey ?? '']?.OffensiveRebounds?.value ??
          0
        })`,
        value: teamStatModel?.[teamStatsLookupKey ?? '']?.Rebounds?.value ?? 0,
      },
      AST: teamStatModel?.[teamStatsLookupKey ?? '']?.Assists ?? {
        display: '',
        value: 0,
      },
      STL: teamStatModel?.[teamStatsLookupKey ?? '']?.Steals ?? {
        display: '',
        value: 0,
      },
      BLK: teamStatModel?.[teamStatsLookupKey ?? '']?.Blocks ?? {
        display: '',
        value: 0,
      },
      TOV: teamStatModel?.[teamStatsLookupKey ?? '']?.Turnovers ?? {
        display: '',
        value: 0,
      },
      FG: {
        display: `${
          teamStatModel?.[teamStatsLookupKey ?? '']?.FieldGoalsMade?.value ?? 0
        }-${
          teamStatModel?.[teamStatsLookupKey ?? '']?.FieldGoalsAttempted
            ?.value ?? 0
        }`,
        value:
          teamStatModel?.[teamStatsLookupKey ?? '']?.FieldGoalsMade?.value ?? 0,
      },
      '3P': {
        display: `${
          teamStatModel?.[teamStatsLookupKey ?? '']?.ThreePointsMade?.value ?? 0
        }-${
          teamStatModel?.[teamStatsLookupKey ?? '']?.ThreePointsAttempted
            ?.value ?? 0
        }`,
        value:
          teamStatModel?.[teamStatsLookupKey ?? '']?.ThreePointsMade?.value ??
          0,
      },
      FT: {
        display: `${
          teamStatModel?.[teamStatsLookupKey ?? '']?.FreeThrowsMade?.value ?? 0
        }-${
          teamStatModel?.[teamStatsLookupKey ?? '']?.FreeThrowsAttempted
            ?.value ?? 0
        }`,
        value:
          teamStatModel?.[teamStatsLookupKey ?? '']?.FreeThrowsMade?.value ?? 0,
      },
      'TS%': teamStatModel?.[teamStatsLookupKey ?? '']
        ?.TrueShootingPercentage ?? { display: '', value: 0 },
    },
  ]

  $: highlightActivePlayers = live
    ? $plays
        .findLast(
          (p): p is LineupChangeEvent =>
            p.type === 'lineupChange' && p.teamId === team?.teamId,
        )
        ?.onCourtPlayerIds.reduce(
          (acc, id) => {
            return {
              ...acc,
              [$players[id]?.longName ?? '']: team?.colors,
            }
          },
          {} as Record<string, Colors | undefined>,
        )
    : undefined
</script>

<div class="flex flex-col gap-3" style:min-height="1200px">
  {#if hasPeriodSplits}
    <div
      class="md:max-w-96 w-full mx-auto relative flex justify-between bg-gray-8 dark:bg-gray-3 rounded-full border border-gray-6 dark:border-gray-3"
    >
      <span
        class="absolute h-full bg-teal dark:bg-gray-7 rounded-full transition-transform ease-out"
        style:width={selectorWidth + '%'}
        style:left={selectedTab * selectorWidth + '%'}
      ></span>
      {#each tabs as tab (tab)}
        <div
          class="flex-1 py-1 text-center relative cursor-pointer"
          class:dark:text-gray-2={selectedTab === tab.value}
          class:text-gray-8={selectedTab === tab.value}
          on:click={() => (selectedTab = tab.value)}
        >
          {tab.label}
        </div>
      {/each}
    </div>
  {/if}

  {#if starterPlayerIds.length > 0}
    <Grid
      textInherit
      disableSort
      data={{
        name: 'starters',
        rows: starterRows,
        columns: playerColumns('Starters'),
      }}
      stickyColumns={['NAME']}
      highlight={highlightActivePlayers}
      onRowClick={onClickSelectPlayer}
      {rowKeyFn}
    />
  {/if}

  <Grid
    textInherit
    disableSort
    data={{
      name: 'bench',
      rows: benchRows,
      columns: playerColumns(starterPlayerIds.length > 0 ? 'Bench' : 'Players'),
    }}
    stickyColumns={['NAME']}
    highlight={highlightActivePlayers}
    onRowClick={onClickSelectPlayer}
    {rowKeyFn}
  />

  <Grid
    textInherit
    disableSort
    data={{ name: 'team', rows: teamRows, columns: teamColumns }}
    stickyColumns={['NAME']}
  />

  {#if live}
    <div class="flex items-center">
      <span
        class="w-4 h-4 mr-2"
        style:background={team?.colors?.backgroundColor}
      ></span>
      On court
    </div>
  {/if}
</div>
